import { useMemo } from 'react';
import type { EditorScreen } from '@/store/editor/types/screen';
import {
  visitorPathFrom,
  type FunnelGraphResult,
} from '@/logic-engine/funnel-graph';

/**
 * Числа шапки, которые ВИДИТ человек (счётчик «N/M», полоса, сегменты,
 * «Шаг N из M»), без экранов с полем `progressSkip: true`
 * (stylepack/progress-skip, слово владельца 21.10.2026: у Dance-bit 36
 * экранов, а счётчик «N/21» — информационные экраны в счёт не идут).
 *
 * «Засчитанный экран» — экран без `progressSkip: true`.
 *   номер = засчитанные в стеке истории + текущий, если он засчитан
 *           (пропускаемый текущий — номер последнего засчитанного), не меньше 1;
 *   всего = засчитанные в истории + засчитанные в остатке пути
 *           (`visitorPathFrom`, тот же путь, что у useQuizVisitorPath.ts),
 *           не меньше номера.
 * Превью редактора (`isControlled`) — то же правило по списку экранов.
 *
 * Числа событий аналитики здесь НЕ считаются: они по-прежнему по всему пути
 * (useQuizVisitorPath.ts, контракт интегратора WEB-1812).
 *
 * В воронке нет ни одного `progressSkip` — `null`: вызывающий берёт прежние
 * числа как есть, байт в байт.
 */
type SkipFlagged = Pick<EditorScreen, 'progressSkip'> | undefined;

const counted = (screen: SkipFlagged): boolean =>
  screen !== undefined && screen.progressSkip !== true;

const countCounted = (list: readonly SkipFlagged[]): number =>
  list.reduce((n, s) => (counted(s) ? n + 1 : n), 0);

export function shownStepCounts(
  screens: readonly EditorScreen[],
  history: readonly number[],
  graph: FunnelGraphResult,
  currentIndex: number,
  isControlled: boolean,
): readonly [number, number] | null {
  if (!screens.some((s) => s.progressSkip === true)) return null;
  const current = screens[currentIndex];
  if (isControlled) {
    const number = Math.max(
      1,
      countCounted(screens.slice(0, currentIndex + 1)),
    );
    return [number, Math.max(countCounted(screens), number)];
  }
  const passed = countCounted(history.map((i) => screens[i]));
  const number = Math.max(1, passed + (counted(current) ? 1 : 0));
  const ahead = current ? visitorPathFrom(graph, current.id) : [];
  // Путь пуст (экран без id, мусорное содержимое) — как у useQuizVisitorPath:
  // всего по списку экранов, только засчитанных.
  let total: number;
  if (ahead.length > 0) {
    const byId = new Map(screens.map((s) => [s.id, s] as const));
    total = passed + countCounted(ahead.map((id) => byId.get(id)));
  } else {
    total = countCounted(screens);
  }
  return [number, Math.max(total, number)];
}

/**
 * Отдаёт [индекс шага с нуля, номер с единицы, всего] для шапки. Без
 * `progressSkip` в воронке — прежние числа плеера (`stepNumber`, `total`).
 * Кортеж и позиционные параметры — как у useQuizVisitorPath (вес первого
 * экрана покупателя).
 */
export function useQuizShownStep(
  screens: EditorScreen[],
  history: readonly number[],
  graph: FunnelGraphResult,
  currentIndex: number,
  isControlled: boolean,
  stepNumber: number,
  total: number,
) {
  return useMemo(() => {
    const shown = shownStepCounts(
      screens,
      history,
      graph,
      currentIndex,
      isControlled,
    );
    const [n, t] = shown ?? [stepNumber, total];
    return [n - 1, n, t] as const;
  }, [screens, history, graph, currentIndex, isControlled, stepNumber, total]);
}
